const AURA_SSO_SESSION_NAME = 'aura';
const AURA_SSO_START_URL = 'https://weaura.awsapps.com/start';
const AURA_SSO_REGION = 'us-east-2';
const AURA_MGMT_ACCOUNT_ID = '950242546328';
const AURA_JUMPER_PROFILE = 'aura-jumper';
const AURA_JUMPER_ROLE_NAME = 'Aura_Jumper';
const DEFAULT_REGION = 'us-east-2';
const READONLY_ROLE_NAME = 'aura';
const JIT_ROLE_SUFFIX = '-aura';

const UNSAFE_SLUG_CHARS = /[^a-z0-9-]+/g;
const MULTI_DASH = /-+/g;

export type AwsCliProfileKind = 'readonly' | 'jit';

export interface AwsCliSnippetInput {
  accountId: string;
  accountName: string;
  requesterEmail?: string | null;
  profileName?: string | null;
  clientSlug?: string | null;
  region?: string;
  kind: AwsCliProfileKind;
}

export interface AwsCliSnippet {
  profileName: string;
  roleArn: string;
  config: string;
  loginCommand: string;
  testCommand: string;
}

export function slugifyEmailLocal(email: string): string {
  if (!email || !email.includes('@')) {
    throw new Error(`Email invalido: ${email}`);
  }

  const local = email.split('@', 1)[0]?.toLowerCase() ?? '';
  const slug = local.replace(UNSAFE_SLUG_CHARS, '-').replace(MULTI_DASH, '-').replace(/^-|-$/g, '');
  if (!slug) {
    throw new Error(`Email gera slug vazio: ${email}`);
  }
  return slug;
}

export function jitRoleName(email: string): string {
  return `${slugifyEmailLocal(email)}${JIT_ROLE_SUFFIX}`;
}

export function buildAwsCliSnippet(input: AwsCliSnippetInput): AwsCliSnippet {
  const region = input.region ?? DEFAULT_REGION;
  const roleArn = buildRoleArn(input);
  const profileName = buildTargetProfileName(input);
  const config = [buildBaseConfig(region), buildTargetProfileConfig(profileName, roleArn, region)]
    .join('\n\n')
    .trimEnd();

  return {
    profileName,
    roleArn,
    config,
    loginCommand: `aws sso login --sso-session ${AURA_SSO_SESSION_NAME}`,
    testCommand: `aws sts get-caller-identity --profile ${profileName}`,
  };
}

export function buildTargetProfileName(input: AwsCliSnippetInput): string {
  const label = slugifySegment(input.clientSlug || input.accountName) || input.accountId;
  const profile =
    input.kind === 'readonly' ? 'readonly' : slugifySegment(input.profileName || 'jit');
  const base = `aura-${label}-${input.accountId}-${profile}`;
  return trimProfileName(base);
}

function buildRoleArn(input: AwsCliSnippetInput): string {
  const roleName =
    input.kind === 'readonly' ? READONLY_ROLE_NAME : jitRoleName(input.requesterEmail ?? '');
  return `arn:aws:iam::${input.accountId}:role/${roleName}`;
}

function buildBaseConfig(region: string): string {
  return [
    `[sso-session ${AURA_SSO_SESSION_NAME}]`,
    `sso_start_url = ${AURA_SSO_START_URL}`,
    `sso_region = ${AURA_SSO_REGION}`,
    'sso_registration_scopes = sso:account:access',
    '',
    `[profile ${AURA_JUMPER_PROFILE}]`,
    `sso_session = ${AURA_SSO_SESSION_NAME}`,
    `sso_account_id = ${AURA_MGMT_ACCOUNT_ID}`,
    `sso_role_name = ${AURA_JUMPER_ROLE_NAME}`,
    `region = ${region}`,
    'output = json',
  ].join('\n');
}

function buildTargetProfileConfig(profileName: string, roleArn: string, region: string): string {
  return [
    `[profile ${profileName}]`,
    `role_arn = ${roleArn}`,
    `source_profile = ${AURA_JUMPER_PROFILE}`,
    `region = ${region}`,
    'output = json',
  ].join('\n');
}

function slugifySegment(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(UNSAFE_SLUG_CHARS, '-')
    .replace(MULTI_DASH, '-')
    .replace(/^-|-$/g, '');
}

function trimProfileName(value: string): string {
  const slug = slugifySegment(value);
  if (slug.length <= 80) return slug;
  return slug.slice(0, 80).replace(/-+$/g, '');
}
